/**
 * Domain configuration utilities for partner white-label domain mapping.
 *
 * Partners can have:
 * 1. A free subdomain: {slug}.app.diiivorce.com
 * 2. A custom domain: divorce.smithlaw.com (via Cloudflare for SaaS)
 */

const PARTNER_SUBDOMAIN_SUFFIX = '.app.diiivorce.com';
const MAIN_DOMAINS = ['diiivorce.com', 'www.diiivorce.com', 'localhost'];

/**
 * Generate a URL-safe slug from a company name.
 * - Lowercases
 * - Removes special characters
 * - Replaces spaces with hyphens
 * - Collapses multiple hyphens
 * - Trims leading/trailing hyphens
 * - Ensures minimum 3 characters
 */
export function slugify(companyName: string): string {
  return companyName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')    // Remove special chars
    .replace(/\s+/g, '-')             // Spaces to hyphens
    .replace(/-+/g, '-')              // Collapse hyphens
    .replace(/^-|-$/g, '')            // Trim leading/trailing hyphens
    .slice(0, 63);                    // Max 63 chars (DNS label limit)
}

/**
 * Validate a subdomain slug format.
 * Must be 3-63 chars, lowercase alphanumeric + hyphens, no leading/trailing hyphens.
 */
export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9]([a-z0-9-]{1,61}[a-z0-9])?$/.test(slug) && slug.length >= 3;
}

/**
 * Get the full subdomain URL for a given slug.
 */
export function getSubdomainUrl(slug: string): string {
  return `https://${slug}${PARTNER_SUBDOMAIN_SUFFIX}`;
}

/**
 * Extract the partner slug from a hostname if it's a partner subdomain.
 * Returns null if the hostname is not a partner subdomain.
 *
 * Examples:
 *   'smith-law.app.diiivorce.com' -> 'smith-law'
 *   'diiivorce.com' -> null
 *   'divorce.smithlaw.com' -> null (custom domain, not a subdomain)
 */
export function getPartnerSlugFromHostname(hostname: string): string | null {
  const suffix = PARTNER_SUBDOMAIN_SUFFIX.replace(/^\./, '');
  if (!hostname.endsWith(suffix)) return null;

  const slug = hostname.slice(0, -(suffix.length + 1)); // +1 for the dot
  if (!slug || slug.includes('.')) return null;

  return slug;
}

/**
 * Check if the current hostname is a partner domain (subdomain or custom).
 * Returns true for any hostname that is NOT the main SettleWise domain or localhost.
 */
export function isPartnerDomain(hostname: string): boolean {
  // Strip port for comparison
  const host = hostname.split(':')[0];

  if (MAIN_DOMAINS.includes(host)) return false;
  if (host.endsWith('.localhost')) return false;

  return true;
}

/**
 * Get the partner config injected by the Cloudflare Worker, if available.
 * The Worker injects window.__PARTNER_CONFIG__ into the HTML head.
 */
export function getInjectedPartnerConfig(): PartnerConfig | null {
  if (typeof window === 'undefined') return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any).__PARTNER_CONFIG__ ?? null;
}

/**
 * Partner config shape injected by the Cloudflare Worker.
 */
export interface PartnerConfig {
  partnerId: string;
  slug: string;
  companyName: string;
  logoUrl: string | null;
  logoDarkUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  fontDisplay: string | null;
  fontBody: string | null;
  tagline: string | null;
  supportEmail: string | null;
  supportPhone: string | null;
  privacyUrl: string | null;
  termsUrl: string | null;
  showPoweredBy: boolean;
}
